import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import {
    OrderCheckoutState,
    SelectedOffer,
    SelectedOffers,
    SelectedAddOns,
    SelectedAddon,
} from '@shared/types';
import { StepContainer } from '../../../StepContainer/index';
import { useOrderCheckoutContext } from '../../../CheckoutContext/OrderContext';
import { OffersSelect } from '../../../OffersSelect';
import { AddonsSection } from '../../../AddonsSection';
import { VouchersSection } from '../../../VoucherSection';
import { isOfferDateValid } from '../../../shared/isOfferDateValid';
import styles from './styles.module.scss';

export type PriceStepProps = {};

export const PriceStep: FunctionComponent<PriceStepProps> = () => {
    const { course, educator, checkoutState, submitResponse, offers, addons } =
        useOrderCheckoutContext();

    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );

    const bookableOffer = useMemo(() => {
        return (
            offers.find((offer) => {
                return isOfferDateValid(offer);
            })?._id || ''
        );
    }, [offers]);
    const [selectedOffers, setSelectedOffers] = useState<SelectedOffers>(
        (checkoutState.stepState.price.state?.offers || []).reduce(
            (initialState: SelectedOffers, selectedOffer: SelectedOffer) => {
                return { ...initialState, [selectedOffer.offerId]: selectedOffer.quantity };
            },
            { [bookableOffer]: 1 }
        )
    );

    const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOns>(
        (checkoutState.stepState.price.state?.addOns || []).reduce(
            (initialState: SelectedAddOns, selectedAddon: SelectedAddon) => {
                return { ...initialState, [selectedAddon.addonId]: selectedAddon.quantity };
            },
            {}
        )
    );

    const selectedOffersArray: SelectedOffer[] = useMemo(() => {
        return Object.entries(selectedOffers).map(([offerId, quantity]) => ({
            offerId,
            quantity,
        }));
    }, [selectedOffers]);

    const selectedAddOnsArray: SelectedAddon[] = useMemo(() => {
        return Object.entries(selectedAddOns).map(([addonId, quantity]) => ({
            addonId,
            quantity,
        }));
    }, [selectedAddOns]);

    const [selectedVoucher, setSelectedVoucher] = useState<string | null>(
        checkoutState.stepState.price.state?.voucherId || null
    );
    const [selectedVoucherCode, setSelectedVoucherCode] = useState<string | null>(null);
    const onVoucherSelected = useCallback((voucherId: string, voucherCode: string) => {
        setSelectedVoucher(voucherId);
        setSelectedVoucherCode(voucherCode);
    }, []);

    const onContinue = useCallback(() => {
        submitResponse({
            actionType: 'order pricing options picked',
            payload: {
                selectedOffers: selectedOffersArray,
                selectedAddons: selectedAddOnsArray,
                appliedPromoCode: selectedVoucherCode || undefined,
            },
        });
    }, [submitResponse, selectedOffersArray, selectedAddOnsArray, selectedVoucherCode]);

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const optimisticCheckoutState = useMemo((): OrderCheckoutState => {
        return {
            ...checkoutState,
            stepState: {
                ...checkoutState.stepState,
                price: {
                    status: 'complete',
                    state: {
                        offers: selectedOffersArray,
                        addOns: selectedAddOnsArray,
                        voucherId: selectedVoucher || undefined,
                        spacesRequested: selectedOffersArray.reduce((total, offer) => {
                            return total + offer.quantity;
                        }, 0),
                    },
                },
            },
        };
    }, [checkoutState, selectedOffersArray, selectedAddOnsArray, selectedVoucher]);

    return (
        <StepContainer
            course={course}
            optimisticCheckoutState={optimisticCheckoutState}
            onContinueClick={onContinue}
            onBackClick={onBackClick}
        >
            <div className={styles.container}>
                <h1>Offers</h1>
                <OffersSelect
                    offers={offers}
                    selectedOffers={selectedOffers}
                    setSelectedOffers={setSelectedOffers}
                    hidePricing={hidePricing}
                    currency={course.currency}
                    availableSpaces={course.spaces || 100}
                />
                <AddonsSection
                    addons={addons}
                    selectedAddOns={selectedAddOns}
                    setSelectedAddOns={setSelectedAddOns}
                    hidePricing={hidePricing}
                    currency={course.currency}
                />
                <VouchersSection
                    offers={offers}
                    courseId={course._id}
                    educatorId={course.educator_ref}
                    onVoucherSelected={onVoucherSelected}
                    origin={checkoutState.origin}
                />
            </div>
        </StepContainer>
    );
};

export default PriceStep;
